/** Canvas painting: brushes, live bodies, and the squash-stretch that sells "alive". */

import type { Body } from './body.ts';
import type { Stroke } from './stroke.ts';
import type { Vec } from './vec.ts';
import { BRUSHES } from './stroke.ts';

/** Deterministic per-stroke noise so crayon grain does not crawl between frames. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function widthAt(stroke: Stroke, i: number): number {
  const spec = BRUSHES[stroke.brush];
  const speed = stroke.speeds[i] ?? 0;
  return Math.max(1, stroke.width * spec.scale * (1 - speed * spec.taper));
}

export function drawStroke(ctx: CanvasRenderingContext2D, stroke: Stroke): void {
  const pts = stroke.points;
  if (pts.length === 0) return;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (pts.length === 1) {
    ctx.fillStyle = stroke.brush === 'rainbow' ? hue(0) : stroke.color;
    ctx.beginPath();
    ctx.arc(pts[0].x, pts[0].y, widthAt(stroke, 0) / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }

  if (stroke.fill && pts.length > 2) {
    ctx.fillStyle = stroke.fill;
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.closePath();
    ctx.fill();
  }

  switch (stroke.brush) {
    case 'crayon':
      paintCrayon(ctx, stroke);
      break;
    case 'pencil':
      paintSimple(ctx, stroke, 0.75);
      break;
    case 'marker':
      paintSimple(ctx, stroke, 1);
      break;
    case 'rainbow':
      paintRainbow(ctx, stroke);
      break;
  }
  ctx.restore();
}

/**
 * A stroke is filled as ONE path built from per-segment quads plus a disc at
 * every point, all wound the SAME way so `nonzero` fill unions them.
 *
 * Two traps here, both of which produced visible defects:
 *   - stroking each segment separately overlaps a round cap on every join, and
 *     at any alpha below 1 those overlaps double into a string of beads;
 *   - building one big outline loop and adding discs winds them oppositely, so
 *     nonzero fill CANCELS the overlaps and scallops chunks out of the line.
 */
function ribbonPath(ctx: CanvasRenderingContext2D, stroke: Stroke, widthScale = 1, jitterX = 0, jitterY = 0): void {
  const pts = stroke.points;
  const n = pts.length;
  const half: number[] = [];
  const normals: Vec[] = [];
  for (let i = 0; i < n; i++) {
    half.push((widthAt(stroke, i) / 2) * widthScale);
    const prev = pts[Math.max(0, i - 1)];
    const next = pts[Math.min(n - 1, i + 1)];
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const len = Math.hypot(dx, dy) || 1;
    normals.push({ x: -dy / len, y: dx / len });
  }

  ctx.beginPath();
  for (let i = 0; i < n - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const na = normals[i];
    const nb = normals[i + 1];
    const ha = half[i];
    const hb = half[i + 1];
    ctx.moveTo(a.x + na.x * ha + jitterX, a.y + na.y * ha + jitterY);
    ctx.lineTo(b.x + nb.x * hb + jitterX, b.y + nb.y * hb + jitterY);
    ctx.lineTo(b.x - nb.x * hb + jitterX, b.y - nb.y * hb + jitterY);
    ctx.lineTo(a.x - na.x * ha + jitterX, a.y - na.y * ha + jitterY);
    ctx.closePath();
  }

  // Discs round the joins and the two ends. Drawn anticlockwise to match the
  // quads above; matching orientation is what keeps nonzero fill a union.
  for (let i = 0; i < n; i++) {
    const r = half[i];
    if (r <= 0.2) continue;
    ctx.moveTo(pts[i].x + r + jitterX, pts[i].y + jitterY);
    ctx.arc(pts[i].x + jitterX, pts[i].y + jitterY, r, 0, Math.PI * 2, true);
  }
}

function paintSimple(ctx: CanvasRenderingContext2D, stroke: Stroke, alpha: number): void {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = stroke.color;
  ribbonPath(ctx, stroke);
  ctx.fill();
  ctx.globalAlpha = 1;
}

/** Wax crayon: a solid core plus two offset grain passes, each a single fill. */
function paintCrayon(ctx: CanvasRenderingContext2D, stroke: Stroke): void {
  const rnd = mulberry32(stroke.seed);
  ctx.fillStyle = stroke.color;
  ctx.globalAlpha = 0.94;
  ribbonPath(ctx, stroke);
  ctx.fill();

  ctx.globalAlpha = 0.22;
  for (let pass = 0; pass < 2; pass++) {
    const w = stroke.width;
    ribbonPath(ctx, stroke, 0.62, (rnd() - 0.5) * w * 0.45, (rnd() - 0.5) * w * 0.45);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

const hue = (h: number): string => `hsl(${((h % 360) + 360) % 360} 92% 58%)`;

/** One ribbon, filled with a gradient — seams would show on a per-segment fill. */
function paintRainbow(ctx: CanvasRenderingContext2D, stroke: Stroke): void {
  const pts = stroke.points;
  const first = pts[0];
  const last = pts[pts.length - 1];
  let spanX = last.x - first.x;
  let spanY = last.y - first.y;
  if (Math.hypot(spanX, spanY) < 1) {
    spanX = 1;
    spanY = 0;
  }
  const gradient = ctx.createLinearGradient(first.x, first.y, first.x + spanX, first.y + spanY);
  const base = stroke.seed * 37;
  for (let i = 0; i <= 6; i++) gradient.addColorStop(i / 6, hue(base + i * 60));
  ctx.fillStyle = gradient;
  ribbonPath(ctx, stroke);
  ctx.fill();
}

export function drawStrokes(ctx: CanvasRenderingContext2D, strokes: Stroke[]): void {
  for (const s of strokes) drawStroke(ctx, s);
}

/** Draw a live body: its own strokes, transformed, with impact squash. */
export function drawBody(ctx: CanvasRenderingContext2D, body: Body): void {
  ctx.save();
  ctx.translate(body.pos.x, body.pos.y);
  ctx.rotate(body.angle);
  if (body.wobble > 0.002) {
    const w = body.wobble;
    ctx.scale(1 + w, 1 - w * 0.85);
  }
  if (body.held) {
    ctx.shadowColor = 'rgba(43, 36, 64, 0.35)';
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 8;
  }
  drawStrokes(ctx, body.strokes);
  ctx.restore();
}

/** Soft contact shadow under a body — cheap depth cue kids read instantly. */
export function drawBodyShadow(ctx: CanvasRenderingContext2D, body: Body, floorY: number): void {
  const gap = Math.max(0, floorY - (body.pos.y + body.radius));
  if (gap > 240) return;
  const t = 1 - gap / 240;
  ctx.save();
  ctx.globalAlpha = 0.16 * t;
  ctx.fillStyle = '#2b2440';
  ctx.beginPath();
  ctx.ellipse(body.pos.x, floorY - 4, body.radius * (0.55 + t * 0.45), body.radius * 0.2 * t + 3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Confetti-ish burst used for taps, pops and celebrations. */
export interface Spark {
  pos: Vec;
  vel: Vec;
  life: number;
  maxLife: number;
  color: string;
  size: number;
}

export function spawnSparks(list: Spark[], at: Vec, colors: string[], count = 10, power = 260): void {
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + Math.random() * 0.5;
    const s = power * (0.4 + Math.random() * 0.8);
    list.push({
      pos: { x: at.x, y: at.y },
      vel: { x: Math.cos(a) * s, y: Math.sin(a) * s - 60 },
      life: 0.5 + Math.random() * 0.4,
      maxLife: 0.9,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 3 + Math.random() * 5,
    });
  }
}

export function updateSparks(list: Spark[], dt: number): void {
  for (let i = list.length - 1; i >= 0; i--) {
    const s = list[i];
    s.life -= dt;
    if (s.life <= 0) {
      list.splice(i, 1);
      continue;
    }
    s.vel.y += 900 * dt;
    s.vel.x *= 0.99;
    s.pos.x += s.vel.x * dt;
    s.pos.y += s.vel.y * dt;
  }
}

export function drawSparks(ctx: CanvasRenderingContext2D, list: Spark[]): void {
  for (const s of list) {
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, s.life / s.maxLife));
    ctx.fillStyle = s.color;
    ctx.translate(s.pos.x, s.pos.y);
    ctx.rotate(s.pos.x * 0.05 + s.life * 6);
    ctx.fillRect(-s.size / 2, -s.size / 2, s.size, s.size * 0.7);
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}
